<script setup lang="ts">
/**
 * 〈代管 › 全部站台〉（`/sites`；operator 以上，批151 C7）。
 *
 * 跨主機的站台清單——以前要找一站只能先猜它在哪台主機、再點進主機詳情。
 *
 * - **過濾一律在頁首的過濾器裡**（產品、客戶、主機、狀態、含已回收）＋關鍵字（站台代號或網域），
 *   全部送伺服器（`GET /api/admin/sites`），清單上方不放分類膠囊。
 * - keyset 分頁：一頁 50 筆、「載入更多」接下一頁；換任何過濾條件＝從第一頁重來（游標跟著 WHERE 走）。
 * - **機密反查**（營運主管以上）：輸入完整值或末四碼，找出哪些在役站台在用。輸入框
 *   `type=password`、送出後立刻清空；結果點進站台詳情。後端只查在役站、稽核只記末四碼。
 *
 * ⚠️ operator 坑：客戶清單 `GET /customers` 是 admin 以上——operator 的客戶下拉改用「清單裡出現過的
 *    客戶」累積出來（不打那支）。主機清單與產品憑證設計 operator 讀得到。
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import FilterClearButton from '@/components/FilterClearButton.vue'
import UiButton from '@/components/UiButton.vue'
import * as hosting from '@/api/hosting'
import type { ManagedHost } from '@/api/hosting'
import * as sitesApi from '@/api/sites'
import type { SiteListQuery, SiteListRow, SiteSecretLookupResult } from '@/api/sites'
import { SITE_STATE_LABELS, siteStateClass, siteStateLabel } from '@/lib/fleet'
import { type LookupMode, lookupBody, siteSecretErrText } from '@/lib/siteSecrets'
import { fmtSeen } from '@/lib/staleness'
import { safeHttpUrl } from '@/lib/safeUrl'
import { customerOptionLabel } from '@/lib/customerLabel'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { vCardTable } from '@/lib/cardTable'

const auth = useAuthStore()
const store = useDataStore()
const route = useRoute()

const PAGE = 50

// --- 過濾器（全部送伺服器）---
// 批186：下拉與勾選記在 `lib/persistedFilters`（重新整理、切頁回來都還在）；`?customer`／`?host`
// 帶進來時整組換成網址的條件。關鍵字不記，但「清除全部條件」會一起清。
const pf = usePersistedFilters(
  'sites',
  { productFilter: '', custFilter: '', hostFilter: '', stateFilter: '', includeDecommissioned: false },
  {
    oneOf: { stateFilter: ['', ...Object.keys(SITE_STATE_LABELS)] },
    deepLink: { route, router: useRouter(), map: { customer: 'custFilter', host: 'hostFilter' } },
  },
)
const { productFilter, custFilter, hostFilter, stateFilter, includeDecommissioned } = pf.refs
const { dirty: filtersDirty } = pf
const keyword = ref('')

const rows = ref<SiteListRow[]>([])
const nextCursor = ref<string | null>(null)
const loading = ref(true)
const loadingMore = ref(false)
const loadError = ref('')
const hosts = ref<ManagedHost[]>([])

/** 狀態下拉：`已回收` 也在裡面（選它＝只看已回收，與「含已回收」勾選是兩件事）。 */
const STATE_OPTIONS = Object.entries(SITE_STATE_LABELS).map(([value, label]) => ({ value, label }))

/** 產品下拉＝我可以經手的代管產品（`/me` 算好的「支援代管 ∩ 我的範圖」）。 */
const productOptions = computed(() =>
  auth.hostingProductKeys.map((k) => ({ value: k, label: store.productByKey(k)?.name || k })),
)

/**
 * 客戶下拉：admin 以上用客戶清單；operator 讀不到那支（403），改用清單裡出現過的客戶
 * ——累積起來，免得選了一位之後下拉只剩他一個、切不回別人。
 */
const seenCustomers = ref<Record<string, string>>({})
const customerOptions = computed(() => {
  if (auth.isAdminOrAbove) return store.customers.map((c) => ({ value: c.id, label: customerOptionLabel(c) }))
  return Object.entries(seenCustomers.value)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

function query(cursor?: string): SiteListQuery {
  return {
    product: productFilter.value || undefined,
    customer_id: custFilter.value || undefined,
    host_id: hostFilter.value || undefined,
    state: stateFilter.value || undefined,
    include_decommissioned: includeDecommissioned.value,
    q: keyword.value.trim() || undefined,
    limit: PAGE,
    cursor,
  }
}

function remember(list: SiteListRow[]) {
  const next = { ...seenCustomers.value }
  for (const r of list) if (r.customer_id && r.customer_name) next[r.customer_id] = r.customer_name
  seenCustomers.value = next
}

/**
 * 換條件的競態保護（請求序號）：每次換條件都 `seq++`，還在飛的請求（第一頁或〈載入更多〉）
 * 回來時序號對不上就整包丟掉——只有最後一組條件的結果會寫進畫面。
 */
let seq = 0
/**
 * 目前這份清單是用哪一組條件查出來的。〈載入更多〉一律沿用它，**不讀畫面上的即時條件**：
 * 關鍵字去抖的 400ms 裡兩者不一樣，拿舊游標配新條件會要到一頁錯的資料（游標跟著 WHERE 走）。
 */
let listQuery: SiteListQuery | null = null

/** 條件一改就清空清單與游標，並作廢所有還在飛的請求。 */
function resetList() {
  seq++
  rows.value = []
  nextCursor.value = null
  listQuery = null
  loadingMore.value = false
  loadError.value = ''
}

async function load() {
  resetList()
  const my = seq
  const q = query()
  loading.value = true
  try {
    const page = await sitesApi.listSites(q)
    if (my !== seq) return
    listQuery = q
    rows.value = page.items
    nextCursor.value = page.next_cursor
    remember(page.items)
    loadError.value = ''
  } catch {
    if (my !== seq) return
    loadError.value = '讀取站台清單失敗'
  } finally {
    if (my === seq) loading.value = false
  }
}

async function loadMore() {
  const cursor = nextCursor.value
  // `loadingMore` 擋連點：同一個游標送兩次會把同一頁接兩遍。
  if (!cursor || !listQuery || loadingMore.value) return
  const my = seq
  loadingMore.value = true
  try {
    const page = await sitesApi.listSites({ ...listQuery, cursor })
    if (my !== seq) return
    rows.value = [...rows.value, ...page.items]
    nextCursor.value = page.next_cursor
    remember(page.items)
  } catch {
    if (my === seq) loadError.value = '讀取下一頁失敗'
  } finally {
    // 過期的請求不碰旗標：換條件時 `resetList()` 已經放掉了，新的〈載入更多〉可能正在跑。
    if (my === seq) loadingMore.value = false
  }
}

// 關鍵字去抖（每打一個字打一次會白花往返）——但舊清單與游標**當下就作廢**（去抖只延後送出）。
// ★ 批186：過濾與關鍵字併成一支 watch——只有關鍵字變才去抖；過濾一變就取消計時器、立刻載入。
//   分兩支的話，「清除全部條件」（過濾＋關鍵字一起清）會先打一次、400ms 後再打一次。
let kwTimer: number | null = null
watch([productFilter, custFilter, hostFilter, stateFilter, includeDecommissioned, keyword], (now, prev) => {
  if (kwTimer !== null) window.clearTimeout(kwTimer)
  kwTimer = null
  if (now.slice(0, 5).some((v, i) => v !== prev[i])) {
    void load()
    return
  }
  resetList()
  loading.value = true
  kwTimer = window.setTimeout(() => {
    kwTimer = null
    void load()
  }, 400)
})

function clearFilters() {
  pf.clear()
  keyword.value = ''
}
onBeforeUnmount(() => {
  if (kwTimer !== null) window.clearTimeout(kwTimer)
})

onMounted(() => {
  void load()
  void store.ensureProducts().catch(() => {})
  if (auth.isAdminOrAbove) void store.ensureCustomers().catch(() => {})
  void hosting
    .listManagedHosts({ includeRetired: true })
    .then((list) => (hosts.value = list))
    .catch(() => {})
})

function productLabel(key: string): string {
  return store.productByKey(key)?.name || key
}

// --- 機密反查（營運主管以上）---
const lookupMode = ref<LookupMode>('value')
const lookupInput = ref('')
const lookupBusy = ref(false)
const lookupError = ref('')
const lookupResult = ref<SiteSecretLookupResult | null>(null)

function setLookupMode(m: LookupMode) {
  lookupMode.value = m
  lookupInput.value = ''
  lookupError.value = ''
}

async function runLookup() {
  const prepared = lookupBody(lookupMode.value, lookupInput.value)
  if (!prepared.ok) {
    lookupError.value = prepared.error
    return
  }
  lookupBusy.value = true
  lookupError.value = ''
  // ★ 送出就清空輸入框：值不留在畫面上（成功或失敗都一樣）。
  lookupInput.value = ''
  try {
    lookupResult.value = await sitesApi.lookupSiteSecret(prepared.body)
  } catch (e) {
    lookupResult.value = null
    lookupError.value = siteSecretErrText(e, '反查失敗')
  } finally {
    lookupBusy.value = false
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>全部站台</h1>
      <span class="count">{{ rows.length }}{{ nextCursor ? '+' : '' }}</span>
      <span class="spacer" />
      <label class="hf">
        <span>產品</span>
        <select v-model="productFilter">
          <option value="">全部產品</option>
          <option v-for="p in productOptions" :key="p.value" :value="p.value">{{ p.label }}</option>
        </select>
      </label>
      <label class="hf">
        <span>客戶</span>
        <select v-model="custFilter">
          <option value="">全部客戶</option>
          <option v-for="c in customerOptions" :key="c.value" :value="c.value">{{ c.label }}</option>
        </select>
      </label>
      <label class="hf">
        <span>主機</span>
        <select v-model="hostFilter">
          <option value="">全部主機</option>
          <option v-for="h in hosts" :key="h.id" :value="h.id">{{ h.name }}</option>
        </select>
      </label>
      <label class="hf">
        <span>狀態</span>
        <select v-model="stateFilter">
          <option value="">全部（在役）</option>
          <option v-for="s in STATE_OPTIONS" :key="s.value" :value="s.value">{{ s.label }}</option>
        </select>
      </label>
      <label class="hf cbf" :class="{ dim: !!stateFilter }" :title="stateFilter ? '已指定狀態時以狀態為準' : undefined">
        <input v-model="includeDecommissioned" type="checkbox" :disabled="!!stateFilter" />
        <span>含已回收</span>
      </label>
      <label class="hf">
        <span>搜尋</span>
        <input v-model="keyword" class="kw" type="search" placeholder="站台代號或網域" />
      </label>
      <FilterClearButton :dirty="filtersDirty || !!keyword.trim()" @click="clearFilters" />
    </div>

    <!-- 機密反查：營運主管以上（後端 `require_manager`、網頁專屬）。 -->
    <div v-if="auth.isManagerOrAbove" class="card lookup">
      <div class="card-head">
        <h3 class="section-title">站台機密反查</h3>
        <span class="spacer" />
        <span class="subtle">只查在役站台；稽核只記末四碼</span>
      </div>
      <form class="lk-row" autocomplete="off" @submit.prevent="runLookup">
        <div class="tabs lk-mode" data-hscroll>
          <button type="button" class="tab" :class="{ active: lookupMode === 'value' }" @click="setLookupMode('value')">
            完整值
          </button>
          <button type="button" class="tab" :class="{ active: lookupMode === 'last4' }" @click="setLookupMode('last4')">
            末四碼
          </button>
        </div>
        <!-- ★ 兩種模式都用 type=password（末四碼也是機密的一部分）；送出即清空。 -->
        <input
          v-model="lookupInput"
          class="lk-input"
          type="password"
          autocomplete="off"
          spellcheck="false"
          :maxlength="lookupMode === 'last4' ? 8 : 600"
          :placeholder="lookupMode === 'last4' ? '輸入末四碼' : '貼上完整的機密值'"
        />
        <UiButton variant="secondary" :loading="lookupBusy" :disabled="!lookupInput.trim()" @click="runLookup">
          反查
        </UiButton>
      </form>
      <p v-if="lookupError" class="err">{{ lookupError }}</p>
      <template v-else-if="lookupResult">
        <p class="subtle res-note">
          {{ lookupResult.mode === 'value' ? '完整值比對' : '末四碼比對（可能有多筆候選）' }}：
          {{ lookupResult.matches.length ? `${lookupResult.matches.length} 筆在役站台使用中` : '沒有在役站台使用這把機密' }}
        </p>
        <div v-if="lookupResult.matches.length" class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table mini">
            <thead>
              <tr><th>站台</th><th>網域</th><th>產品</th><th>客戶</th><th>主機</th><th>欄位</th><th>末四碼</th><th>設定時間</th></tr>
            </thead>
            <tbody>
              <tr v-for="m in lookupResult.matches" :key="`${m.site_id}:${m.key}`">
                <td>
                  <RouterLink :to="{ name: 'site-detail', params: { id: m.host_id, siteId: m.site_id } }">{{ m.slug }}</RouterLink>
                  <span class="status-pill sm" :class="siteStateClass(m.state)">{{ siteStateLabel(m.state) }}</span>
                </td>
                <td>{{ m.domain ?? '—' }}</td>
                <td>{{ productLabel(m.product) }}</td>
                <td>{{ m.customer_name ?? '—' }}</td>
                <td>{{ m.host_name ?? '—' }}</td>
                <td><code class="mono">{{ m.env }}</code></td>
                <td class="nowrap">{{ m.last4 ? `…${m.last4}` : '—' }}</td>
                <td class="nowrap">{{ fmtSeen(m.set_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <div v-if="loading && !rows.length" class="loading-wrap"><span class="spinner lg" /></div>
    <div v-else class="card">
      <p v-if="loadError" class="err">{{ loadError }}</p>
      <div v-if="!rows.length" class="empty">
        <div class="empty-title">沒有符合條件的站台</div>
        <div class="empty-sub">已回收的站台預設不列（勾「含已回收」或在狀態選「已回收」）。開新站台請從主機詳情進入開站精靈。</div>
      </div>
      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr><th>站台</th><th>網域</th><th>產品</th><th>客戶</th><th>主機</th><th>狀態</th><th>版本</th><th>建立時間</th></tr>
          </thead>
          <tbody>
            <tr v-for="s in rows" :key="s.id">
              <td>
                <div class="cell2">
                  <RouterLink class="slug" :to="{ name: 'site-detail', params: { id: s.host_id, siteId: s.id } }">{{ s.slug }}</RouterLink>
                  <code class="mono sub">{{ s.instance_id.slice(0, 16) }}</code>
                </div>
              </td>
              <td>
                <a v-if="safeHttpUrl(s.public_url)" :href="safeHttpUrl(s.public_url) ?? undefined" target="_blank" rel="noopener">{{ s.domain ?? s.public_url }}</a>
                <span v-else>{{ s.domain ?? s.public_url ?? '—' }}</span>
              </td>
              <td>{{ productLabel(s.product) }}</td>
              <td>{{ s.customer_name ?? '—' }}</td>
              <td>
                <div class="cell2">
                  <RouterLink :to="{ name: 'host-detail', params: { id: s.host_id } }">{{ s.host_name ?? '—' }}</RouterLink>
                  <span v-if="s.host_reseller_name" class="sub">代理商：{{ s.host_reseller_name }}</span>
                </div>
              </td>
              <td><span class="status-pill" :class="siteStateClass(s.state)">{{ siteStateLabel(s.state) }}</span></td>
              <td class="nowrap">{{ s.app_version ?? s.cs_app_version ?? '—' }}</td>
              <td class="nowrap">{{ fmtSeen(s.created_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="nextCursor" class="more">
        <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.hf {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--fg-3);
  white-space: nowrap;
}
.cbf.dim {
  opacity: 0.55;
}
.kw {
  width: 180px;
}
.err {
  margin: 0 0 var(--sp-3);
  color: var(--danger);
  font-size: var(--fs-12);
}
.lookup {
  margin-bottom: var(--sp-4);
}
.lk-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-2);
}
.lk-mode {
  margin: 0;
}
.lk-input {
  flex: 1 1 260px;
  max-width: 420px;
  min-width: 0;
}
.res-note {
  margin: var(--sp-3) 0 var(--sp-2);
  font-size: var(--fs-12);
}
.tbl.mini {
  font-size: var(--fs-12);
}
.cell2 {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.slug {
  font-weight: 600;
}
.sub {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.nowrap {
  white-space: nowrap;
}
.status-pill.sm {
  margin-left: 6px;
}
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  margin: 0;
  border-top: 1px solid var(--border);
}
</style>
